import React, {
  forwardRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, parseSelectOptions } from "utils/formUtils";
import message from "components/CustomMessage";
import GenericMappingForm from "../GenericMappingForm";
import { Form } from "antd";
import { isEmpty, split } from "utils/lodashUtils";
import { map } from "lodash";

const AWSSecurityHubMappingForm = forwardRef(function AWSSecurityHubMappingForm(
  props,
  ref
) {
  const editModeRef = useRef(!isEmpty(props.record));
  const editModeRefforProduct = useRef(!isEmpty(props.record));

  const [form] = Form.useForm();
  const [awsAccountList, setAWSAccountList] = useState(() => {
    if (props.record && props.record.awsAccount) {
      const { region, awsAccount } = props.record;
      return {
        [region]: split(awsAccount, ","),
      };
    }
    return {};
  });
  const [productList, setProductList] = useState(() => {
    if (props.record && props.record.awsProduct) {
      const { region, awsProduct } = props.record;
      return {
        [region]: split(awsProduct, ","),
      };
    }
    return {};
  });
  const [region, setRegion] = useState(() => {
    if (props.record) {
      const { region } = props.record;
      return region;
    }
    return "";
  });

  const fetchAWSAccounts = useCallback(
    (region) => {
      if (
        (editModeRef.current && region) ||
        (region && !awsAccountList[region])
      ) {
        editModeRef.current = false;
        const { promise, cancel } =
          ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
            login_id: props.loginConfigId,
            type: "awsAccount",
            project: region,
          });

        promise
          .then((response) => {
            const { projects } = response;
            setAWSAccountList((awsAccountList) => ({
              ...awsAccountList,
              [region]: projects,
            }));
          })
          .catch((err) => {
            if (!err?.response?.data?.message) {
              message.error("Problem fetching regions");
            }
          });

        return cancel;
      }
    },
    [props.loginConfigId, props.toolName, awsAccountList]
  );

  const fetchProducts = useCallback(
    (region) => {
      if (
        (editModeRefforProduct.current && region) ||
        (region && !productList[region])
      ) {
        editModeRefforProduct.current = false;
        const { promise, cancel } =
          ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
            login_id: props.loginConfigId,
            type: "product",
            project: region,
          });

        promise
          .then((response) => {
            const { projects } = response;
            setProductList((prodList) => ({
              prodList,
              [region]: projects,
            }));
          })
          .catch((err) => {
            if (!err?.response?.data?.message) {
              message.error("Problem fetching AWS products");
            }
          });

        return cancel;
      }
    },
    [props.loginConfigId, props.toolName, productList]
  );

  const fetchRegions = useCallback(() => {
    let { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(
      props.toolName,
      {
        login_id: props.loginConfigId,
        type: "region",
      }
    );
    promise = promise
      .then((response) => {
        const { projects } = response;
        return projects;
      })
      .catch((err) => {
        message.error("Problem fetching regions");
      });

    return { promise, cancel };
  }, [props.loginConfigId, props.toolName]);

  const getExtraFieldsData = useCallback((config) => {
    const { awsAccount, region, awsProduct } = config;

    return {
      awsAccount: !isEmpty(awsAccount) ? awsAccount.join() : undefined,
      region,
      awsProduct: awsProduct ? awsProduct.join() : undefined,
    };
  }, []);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { awsAccount, region, awsProduct } = config;
    setRegion(region);
    return {
      awsAccount: awsAccount ? split(awsAccount, ",") : undefined,
      region,
      awsProduct: awsProduct ? split(awsProduct, ",") : undefined,
    };
  }, []);

  useEffect(() => fetchAWSAccounts(region), [region, fetchAWSAccounts]);
  useEffect(() => fetchProducts(region), [region, fetchProducts]);

  const onRegionChange = useCallback(
    (value, field) => {
      setRegion(value);
      const { name } = field;
      const configurations = form.getFieldValue("configurations") || [];
      let updatedConfiguration = [...configurations];
      updatedConfiguration[name] = {
        ...updatedConfiguration[name],
        awsAccount: undefined,
        awsProduct: undefined,
      };
      form.setFieldsValue({ configurations: updatedConfiguration });
    },
    [form]
  );

  const extraFields = useMemo(
    () => [
      {
        name: "region",
        colSpan: 8,
        type: FieldTypes.SELECT,
        label: "Region",
        required: true,
        props: {
          api: fetchRegions,
          autoFocus: true,
          onChange: onRegionChange,
        },
      },
      {
        name: "awsAccount",
        colSpan: 8,
        type: FieldTypes.MULTI_SELECT,
        label: "AWS Account",
        props: {
          placeholder: "Leave blank to select all",
          getFormListOptions: (fieldKey) => {
            const configs = form.getFieldValue("configurations");
            const region = configs[fieldKey]?.region;
            return region ? map(awsAccountList[region], option => {
              const optionValue = option.id === option.name ? option.id : `${option.id} - ${option.name}`;
              return {
              label: optionValue,
              value: optionValue,
            }}) : [];
          },
          setExtraProps: (field, listField) => {
            const allConfigs = form.getFieldsValue().configurations;
            const region = allConfigs[listField.name]?.region;
            return {
              disabled: !region,
            };
          },
        },
      },
      {
        name: "awsProduct",
        label: "AWS Product",
        colSpan: 8,
        type: FieldTypes.MULTI_SELECT,
        props: {
          placeholder: "Leave blank to select all",
          getFormListOptions: (fieldKey) => {
            const configs = form.getFieldValue("configurations");
            const region = configs[fieldKey]?.region;
            return region ? parseSelectOptions(productList[region]) : [];
          },
          setExtraProps: (field, listField) => {
            const allConfigs = form.getFieldsValue().configurations;
            const region = allConfigs[listField.name]?.region;
            return {
              disabled: !region,
            };
          },
        },
      },
    ],
    [awsAccountList, fetchRegions, onRegionChange, productList, form]
  );
  return (
    <GenericMappingForm
      ref={ref}
      {...props}
      form={form}
      extraFields={extraFields}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
    />
  );
});

export default AWSSecurityHubMappingForm;
